import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx, DEFAULT_COLORS } from "./api";

/* ═══════════════════════════════════════════════════════════════════════════
   CREATE/EDIT SERVICE MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const ServiceFormModal: React.FC<{
  open: boolean;
  service: any | null;
  onSave: (data: any) => void;
  onClose: () => void;
  saving: boolean;
}> = ({ open, service, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(DEFAULT_COLORS[0]);
  const [status, setStatus] = useState("active");

  useEffect(() => {
    if (service) {
      setName(service.name || "");
      setDescription(service.description || "");
      setColor(service.color || DEFAULT_COLORS[0]);
      setStatus(service.status || "active");
    } else {
      setName("");
      setDescription("");
      setColor(DEFAULT_COLORS[0]);
      setStatus("active");
    }
  }, [service, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MiscellaneousServicesOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {service?.id ? "Edit Service" : "New Service"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          label="Service Name *" size="small" value={name}
          onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx}
        />
        <TextField
          label="Description" size="small" value={description}
          onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            select label="Status" size="small" value={status}
            onChange={(e) => setStatus(e.target.value)} sx={inputSx}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Box />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, mb: 1, textTransform: "uppercase" }}>
            Color Tag
          </Typography>
          <Box sx={{ display: "flex", gap: 0.8 }}>
            {DEFAULT_COLORS.map((c) => (
              <Box
                key={c} onClick={() => setColor(c)}
                sx={{
                  width: 28, height: 28, borderRadius: "50%", bgcolor: c, cursor: "pointer",
                  border: color === c ? `3px solid ${T.navy}` : "3px solid transparent",
                  transition: "all 0.15s", "&:hover": { transform: "scale(1.15)" },
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button
          variant="contained" onClick={() => onSave({ id: service?.id, name: name.trim(), description, color, status })}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}
        >
          {saving ? "Saving..." : "Save Service"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SERVICES LIST PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const ServicesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [saving, setSaving] = useState(false);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editService, setEditService] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.append("status", filterStatus);
    if (search) params.append("search", search);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const res = await api.get(`/banquet/services-list${qs}`);
    if (res.code === 200) setServices(res.data ?? []);
    setLoading(false);
  }, [filterStatus, search]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const filtered = useMemo(() => services, [services]);

  const totalStats = useMemo(() => {
    return {
      categories: services.reduce((s, svc) => s + (svc.categories_count ?? 0), 0),
      subcategories: services.reduce((s, svc) => s + (svc.subcategories_count ?? 0), 0),
      items: services.reduce((s, svc) => s + (svc.items_count ?? 0), 0),
    };
  }, [services]);

  const handleSave = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/service", data);
    if (res.code === 200) {
      showMessage(data.id ? "Service updated" : "Service created");
      setFormOpen(false);
      setEditService(null);
      fetchServices();
    } else {
      showErrorMessage(res.message || "Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await api.del(`/banquet/service-tree/service/${deleteTarget.id}`);
    if (res.code === 200) { showMessage("Service deleted"); fetchServices(); }
    else showErrorMessage(res.message);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const thSx = { fontWeight: 700, fontSize: 11, color: T.slate, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 } as const;

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>
            Services
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>
            Manage DCC service catalogue — categories, subcategories, and items
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={() => { setEditService(null); setFormOpen(true); }}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}
        >
          Add Service
        </Button>
      </Box>

      {/* ── Summary Stats ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 3 }}>
        {[
          { label: "Services", value: services.length, icon: <MiscellaneousServicesOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />, bg: `${T.gold}12` },
          { label: "Categories", value: totalStats.categories, icon: <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />, bg: "#3b82f612" },
          { label: "Subcategories", value: totalStats.subcategories, icon: <FolderOpenOutlinedIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />, bg: "#8b5cf612" },
          { label: "Items", value: totalStats.items, icon: <InventoryOutlinedIcon sx={{ fontSize: 18, color: T.greenTx }} />, bg: T.greenBg },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: stat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {stat.icon}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.navy, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt }}>{stat.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Filters ── */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
        <TextField
          size="small" placeholder="Search services..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, minWidth: 280 }}
        />
        <TextField
          select size="small" value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          label="Status" sx={{ ...inputSx, minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        {(filterStatus || search) && (
          <Button size="small" onClick={() => { setSearch(""); setFilterStatus(""); }}
            sx={{ textTransform: "none", fontSize: 11, color: T.slateLt }}>Clear</Button>
        )}
      </Box>

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: T.gold }} />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <MiscellaneousServicesOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt }}>No services found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: T.bg }}>
                <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                <TableCell sx={thSx}>Service</TableCell>
                <TableCell align="center" sx={thSx}>Categories</TableCell>
                <TableCell align="center" sx={thSx}>Subcategories</TableCell>
                <TableCell align="center" sx={thSx}>Items</TableCell>
                <TableCell align="center" sx={thSx}>Status</TableCell>
                <TableCell align="right" sx={thSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((svc: any, idx: number) => (
                <TableRow
                  key={svc.id} hover
                  sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fef6ee" }, transition: "background 0.15s" }}
                  onClick={() => navigate(`/banquet/services-manager/${svc.id}`)}
                >
                  <TableCell sx={{ py: 1.5, fontSize: 12, color: T.slateLt, width: 40 }}>{idx + 1}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: svc.color ?? T.slateLt, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{svc.name}</Typography>
                        {svc.description && (
                          <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.2 }} noWrap>{svc.description}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Chip label={svc.categories_count} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#3b82f612", color: "#3b82f6" }} />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Chip label={svc.subcategories_count} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#8b5cf612", color: "#8b5cf6" }} />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Chip label={svc.items_count} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Chip
                      label={svc.status ?? "active"} size="small"
                      sx={{
                        height: 22, fontSize: 10, fontWeight: 600,
                        bgcolor: (svc.status ?? "active") === "active" ? T.greenBg : T.redBg,
                        color: (svc.status ?? "active") === "active" ? T.greenTx : T.redTx,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/banquet/services-manager/${svc.id}`)} sx={{ color: T.gold }}>
                        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage Tree">
                      <IconButton size="small" onClick={() => navigate(`/banquet/services-manager/${svc.id}/tree`)} sx={{ color: T.slateLt, "&:hover": { color: "#3b82f6" } }}>
                        <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditService(svc); setFormOpen(true); }} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteTarget({ id: svc.id, name: svc.name })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modals */}
      <ServiceFormModal open={formOpen} service={editService} onSave={handleSave} onClose={() => { setFormOpen(false); setEditService(null); }} saving={saving} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Delete service?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Delete <strong>{deleteTarget?.name}</strong> and all its categories, subcategories, and items?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>
            {isDeleting ? "..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ServicesListPage;
